import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AMPERSAND_STRING,
  COMMENTS_PATH,
  EMPTY_STRING,
  FILTER_SENIORITY_BEHAVIOR_COMMENTS,
  GET_ADDITIONAL_COMMENTS,
  GET_ASSESSMENT_COMMENTS_PATH,
  GET_BEHAVIORS_ATTACHMENTS,
  GET_BEHAVIORS_COMMENT,
  GET_COMMENTS_GENERAL_PATH,
  GET_GENERAL_COMMENT,
  GET_GRADE_CONVERSION_SETTINGS_PATH,
  GET_INSTANCE_VALORATION_PATH,
  GET_LAST_VALORATION_PATH,
  GET_MAP_DEFINITIONS_PATH,
  GET_PRAGMAPOWER_DEFINITIONS_PATH,
  GET_SCORES_PATH,
  GET_VALIDATE_SELF_ASSESSMENT,
  INITIAL_PAGE,
  PAGE_SIZE,
  POST_FINALIZED_AUTOVALORATION,
  POST_SAVE_PARTIAL_ASSESSMENT,
  PUT_ASSESSMENT_APPROVE_PATH,
  PUT_ASSESSMENT_REJECT_PATH,
  PUT_SEND_ASSESSMENT_TO_LEADERS_PATH,
  PUT_SEND_RESULT_PRAGMATIC_PATH,
  SLASH_STRING,
  URL_QUERY_START,
  getAdministrativeLeaderStatusByIdLeader,
  getAssessmentBehaviorById,
  getAssessmentExecutionStatusNotificationByIdAssessment,
  getAssessmentExecutionsByAssessmentExecutionId,
  getAssessmentExecutionsSelfAssessedByAssessmentId,
  getAssessmentExecutionsSuperByAssessmentId,
  getAssessmentPragmaPowersByAssessmentId,
  getAssessmentTraceabilityPath,
  getCommentsById,
  getPragmaPowerBehaviorDefinitionsByCompetenceId,
  getQueryParamsString,
  getValorationStatusActive,
  responseStatus,
} from '@app/constants';
import { AssessmentPragmaPower } from '@app/interface/assesment-pragmapower';
import {
  AssessmentTraceabilityEvent,
  AssessmentTraceabilityResponse,
} from '@app/interface/assessment-traceability-event';
import {
  AssessmentComments,
  BehaviorAttachment,
  BehaviorComment,
  Behaviors,
  DataCompetence,
  MapDefinition,
  Overview,
  PayloadFinalizedAutovaloration,
  PayloadGetInstanceValoration,
  PayloadGetLastValoration,
  PayloadSendAssessmentToLeaders,
  PostBehaviorComment,
  PostSavePartialAssessment,
  Scores,
  SendResultPragmaticRequest,
  SendResultPragmaticResponse,
  StartAssessment,
  StepStatus,
  SuperCommentEndpointPayload,
} from '@app/interface/assessment';
import { AssessmentBehavior } from '@app/interface/assessment-behavior';
import {
  AttachmentInterface,
  AttachmentsInterface,
  PayloadAttachment,
  PayloadAttachmentFilter,
  mockAttachment,
} from '@app/interface/attachment.response';
import {
  CommentInterface,
  CommentsInterface,
  PayloadComment,
  PayloadCommentFilter,
} from '@app/interface/comment.response';
import { PragmaPowerDefinition } from '@app/interface/pragma-power-definition.interface';
import { ServiceType } from '@app/interface/service';
import {
  PayloadGetStatusActiveValoration,
  Valoration,
} from '@app/interface/valoration-last-assessment.interface';
import { BehaviorSubject, Observable, Subject, catchError, from, of, throwError } from 'rxjs';
import { GatewayService, doGetResponseType } from './gateway.service';

@Injectable({
  providedIn: 'root',
})
export class AssessmentService {
  private readonly loadingSubject = new BehaviorSubject(false);
  private readonly operationCompleteSubject = new Subject<AssessmentBehavior[]>();
  operationComplete$ = this.operationCompleteSubject.asObservable();

  constructor(public gateway: GatewayService) {}

  setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  isLoading(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  getBehaviorScores(
    type: 'TeamTechnical' | 'Technical',
    roleType: 'Evaluatee' | 'Evaluator' | 'Shared',
    page: number = 0,
    page_size: number = 20
  ): Observable<Scores[]> {
    const params = {
      type,
      page,
      page_size,
      roleType,
    };
    return from(
      this.gateway
        .doGet<{ data: Scores[] }>(GET_SCORES_PATH, params, ServiceType.AssessmentService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }

  getBehaviors(
    competenceId: number,
    chapter: string,
    seniority: string,
    speciality: string
  ): Observable<Behaviors[]> {
    const path = getPragmaPowerBehaviorDefinitionsByCompetenceId(competenceId);

    const params = {
      chapter,
      seniority,
      speciality,
    };

    return from(
      this.gateway
        .doGet<{ data: Behaviors[] }>(path, params, ServiceType.AssessmentService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }

  getMapDefinitions(
    chapter: string,
    seniority: string,
    speciality: string
  ): Observable<MapDefinition> {
    const queryParamsString = getQueryParamsString({
      page: '0',
      page_size: '20',
      chapter,
      seniority,
      speciality,
    });
    const path = `${GET_MAP_DEFINITIONS_PATH}${URL_QUERY_START}${queryParamsString}`;

    return from(
      this.gateway
        .doGet<MapDefinition>(path, {}, ServiceType.AssessmentsPA)
        .then((response: doGetResponseType) => {
          if (response.success) {
            return {
              success: true,
              chapters: response.data,
            };
          }
          return {
            success: false,
          } as MapDefinition;
        })
    );
  }

  getOverview(
    email: string,
    status: string,
    chapterName: string,
    self_assessed: number,
    pragmapower_type: string
  ): Observable<Overview | null> {
    const queryParamsString = getQueryParamsString({
      person_email: email,
      chapter_name: chapterName,
      status,
      self_assessed: self_assessed.toString(),
      pragmapower_type,
    });

    const path = `${GET_ASSESSMENT_COMMENTS_PATH}${URL_QUERY_START}${queryParamsString}`;
    return from(
      this.gateway
        .doGet<Overview | null>(path, {}, ServiceType.AssessmentService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data as Overview | null;
          }
          return null;
        })
    );
  }

  getValorationStatusActive(payload: PayloadGetStatusActiveValoration): Observable<Valoration> {
    const path: string = getValorationStatusActive();
    return from(
      this.gateway
        .doPost<Valoration>(path, payload, ServiceType.AdministrationService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data as Valoration;
          }
          return response.data as Valoration;
        })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  validateSelfAssessment(
    email: string,
    chapterId: number,
    specialityId: number,
    technologyId: number
  ): Observable<StepStatus[] | null> {
    const queryParamsString = getQueryParamsString({
      person_email: email,
      chapter_id: chapterId,
      speciality_id: specialityId,
      technology_id: technologyId,
    });

    const path = `${GET_INSTANCE_VALORATION_PATH}${GET_VALIDATE_SELF_ASSESSMENT}${URL_QUERY_START}${queryParamsString}`;
    return from(
      this.gateway
        .doGet<StepStatus | null>(path, {}, ServiceType.AdministrationService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data as StepStatus[] | null;
          }
          return null;
        })
    );
  }

  getCompetencies(
    chapter: string,
    seniority: string,
    speciality: string,
    page: number = 0,
    pageSize: number = 30
  ): Observable<PragmaPowerDefinition[]> {
    const params = {
      page,
      page_size: pageSize,
      chapter,
      seniority,
      speciality,
    };

    return from(
      this.gateway
        .doGet<{
          data: PragmaPowerDefinition[];
        }>(GET_PRAGMAPOWER_DEFINITIONS_PATH, params, ServiceType.AssessmentService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            const pragmaPowerDefinitions = response.data?.data ?? [];
            return pragmaPowerDefinitions.map(
              (data) =>
                ({
                  id: data.id,
                  name: data.name,
                  type: data.type,
                  seniority: seniority,
                }) as unknown as PragmaPowerDefinition
            );
          }
          return [];
        })
    );
  }

  startAssessment(body: Record<string, unknown>): Observable<StartAssessment> {
    return from(
      this.gateway
        .doPost<StartAssessment>(
          GET_ASSESSMENT_COMMENTS_PATH,
          { ...body, pragmaPowerType: 1 },
          ServiceType.AssessmentService
        )
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data ?? ({} as StartAssessment);
          }
          return {} as StartAssessment;
        })
    );
  }

  sendAssessmentToLeaders(payload: PayloadSendAssessmentToLeaders): Observable<void> {
    return from(
      this.gateway
        .doPut<void>(
          PUT_SEND_ASSESSMENT_TO_LEADERS_PATH,
          payload,
          ServiceType.AdministrationService
        )
        .then(() => undefined)
    );
  }

  updateAssessmentLeadersReviewStatus(
    idLeader: number,
    body: {
      status: string;
    }
  ): Observable<void> {
    const path = getAdministrativeLeaderStatusByIdLeader(idLeader);

    return from(
      this.gateway.doPut<void>(path, body, ServiceType.AssessmentService).then(() => undefined)
    );
  }

  putFinalizeAssessment(
    idAssessment: number,
    body: {
      seniority?: string;
      level?: string;
      assessmentExecutionStatus?: string;
      users: string[];
      idAssessment?: number;
      comment: string;
    }
  ): Observable<StartAssessment> {
    const path = getAssessmentExecutionStatusNotificationByIdAssessment(idAssessment);

    return from(
      this.gateway
        .doPut<StartAssessment>(path, body, ServiceType.AssessmentService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data ?? ({} as StartAssessment);
          }
          return {} as StartAssessment;
        })
    );
  }

  getQualifiedCompetences(
    idAssessment: number,
    seniorityId: number,
    page = 0,
    pageSize = 30
  ): Observable<DataCompetence[]> {
    const path = getAssessmentPragmaPowersByAssessmentId(idAssessment);
    const params = {
      seniority_id: seniorityId,
      page,
      page_size: pageSize,
    };

    if (idAssessment !== undefined) {
      return from(
        this.gateway
          .doGet<{ data: AssessmentPragmaPower[] }>(path, params, ServiceType.AssessmentService)
          .then((response) => {
            if (response.success || response.status === HttpStatusCode.Ok) {
              const dataCompetences = response.data?.data ?? [];
              return dataCompetences.map(
                (dataCompetence) =>
                  ({
                    idQualifiedCompentece: dataCompetence.id,
                    id: dataCompetence.pragmaPowerDefinition.id,
                    name: dataCompetence.pragmaPowerDefinition.name,
                    score: dataCompetence.score,
                  }) as DataCompetence
              );
            }
            return [];
          })
      );
    }

    return of([]);
  }

  getQualifiedBehaviors(
    idQualifedCompetence: number,
    page = 0,
    pageSize = 30
  ): Observable<AssessmentBehavior[]> {
    const path = getAssessmentBehaviorById(idQualifedCompetence);

    const params = {
      page,
      page_size: pageSize,
    };

    return from(
      this.gateway
        .doGet<{ data: AssessmentBehavior[] }>(path, params, ServiceType.AssessmentService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.data ?? [];
          }
          return [];
        })
    );
  }

  postComment(
    idAssessment: number,
    idBehavior: number,
    reviewerEmail: string,
    comment: string,
    emailUserQualified: string
  ): Observable<any> {
    const body = {
      behaviorDefinitionId: idBehavior,
      pragmaPowerDefinitionId: null,
      assessmentExecutionId: idAssessment,
      personEmail: emailUserQualified,
      comment: comment,
      commentedByEmail: reviewerEmail,
    };
    return from(
      this.gateway
        .doPost(COMMENTS_PATH, body, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data;
          }
          return {
            success: false,
          };
        })
    );
  }

  postCommentGeneral(
    idAssessment: number,
    idBehavior: number | null = null,
    reviewerEmail: string,
    comment: string,
    emailUserQualified: string
  ): Observable<any> {
    const body = {
      behaviorDefinitionId: null,
      pragmaPowerDefinitionId: idBehavior,
      assessmentExecutionId: idAssessment,
      personEmail: emailUserQualified,
      comment: comment,
      commentedByEmail: reviewerEmail,
    };
    return from(
      this.gateway
        .doPost(COMMENTS_PATH, body, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data;
          }
          return {
            success: false,
          };
        })
    );
  }

  getComments(idBehavior: number, page = INITIAL_PAGE, pageSize = PAGE_SIZE): Observable<any> {
    const path = `${GET_COMMENTS_GENERAL_PATH}${idBehavior}`;
    const params = {
      page,
      page_size: pageSize,
    };
    return from(
      this.gateway
        .doGet(path, params, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === responseStatus.success) {
            return response.data.data;
          }
          return {
            success: false,
          };
        })
    );
  }

  getCommentsGeneral(
    idBehavior: number,
    page: number = INITIAL_PAGE,
    pageSize: number = PAGE_SIZE
  ): Observable<any> {
    const path = `${GET_COMMENTS_GENERAL_PATH}${idBehavior}`;
    const params = {
      page,
      page_size: pageSize,
    };
    return from(
      this.gateway
        .doGet(path, params, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === responseStatus.success) {
            return response.data.data;
          }
          return {
            success: false,
          };
        })
    );
  }

  putComment(idBehavior: number, commentedByEmail: string, comment: string) {
    const path = getCommentsById(idBehavior);
    const params = {
      commentedByEmail: commentedByEmail,
      comment: comment,
    };
    return from(
      this.gateway
        .doPut(path, params, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === responseStatus.success) {
            return response.data;
          }
          return {
            success: false,
          };
        })
    );
  }

  savePartialAssessment(payload: PostSavePartialAssessment): Observable<doGetResponseType> {
    const path = POST_SAVE_PARTIAL_ASSESSMENT;

    return from(
      this.gateway
        .doPost(path, payload, ServiceType.MapRatingService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === responseStatus.success) {
            return {
              success: response.success,
            };
          }
          return {
            success: false,
          };
        })
        .catch((error) => {
          return error;
        })
    );
  }

  deleteComment(idComment: number, commenterId: number) {
    const path = `/${idComment}?commenterId=${commenterId}`;
    return from(
      this.gateway
        .doDelete(path, ServiceType.CommentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === responseStatus.success) {
            return response.data;
          }
          return {
            success: false,
          };
        })
    );
  }

  finalizeAutoAssessment(
    assessmentExecutionId: number,
    body: {
      behaviorScores: { id: number; scoreId: number }[];
      speciality: string;
    }
  ): Observable<any> {
    const path = getAssessmentExecutionsSelfAssessedByAssessmentId(assessmentExecutionId);
    return from(
      this.gateway
        .doPost(path, body, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data;
          }
          return {
            success: false,
          };
        })
    );
  }

  emitCompleteOperation(datos: AssessmentBehavior[]) {
    if (!datos) return;
    this.operationCompleteSubject.next(datos);
  }

  getScoreLimit(filter: string[], page: number = 1, page_size: number = 20): Observable<any> {
    const queryParamsString = getQueryParamsString({
      filter: JSON.stringify(filter),
      page: page.toString(),
      page_size: page_size.toString(),
    });
    const path = [GET_GRADE_CONVERSION_SETTINGS_PATH, URL_QUERY_START, queryParamsString].join(
      EMPTY_STRING
    );
    const params = {
      filter,
      page,
      page_size,
    };
    return from(
      this.gateway
        .doGet(path, params, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data;
          }
          return {
            success: false,
          };
        })
    );
  }

  putCommentViewed(idComment: number, data: Record<string, unknown>) {
    const path = getCommentsById(idComment);
    return from(
      this.gateway
        .doPut(path, data, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data;
          }
          return {
            success: false,
          };
        })
    );
  }

  postSuperComments(
    payload: SuperCommentEndpointPayload,
    assessmentExecutionId: number
  ): Observable<any> {
    const path = getAssessmentExecutionsSuperByAssessmentId(assessmentExecutionId);
    return from(
      this.gateway
        .doPost(path, payload, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === responseStatus.success) {
            return response.data;
          }
          return {
            success: false,
          };
        })
    );
  }

  getBehaviorsComments(
    assessmentExecutionId: number,
    seniorityId: number
  ): Observable<BehaviorComment[]> {
    const path = [
      getAssessmentExecutionsByAssessmentExecutionId(assessmentExecutionId),
      URL_QUERY_START,
      FILTER_SENIORITY_BEHAVIOR_COMMENTS,
      seniorityId,
      AMPERSAND_STRING,
      GET_BEHAVIORS_COMMENT,
    ].join(EMPTY_STRING);

    return from(
      this.gateway
        .doGet<{
          seniority: { comments: BehaviorComment[] };
        }>(path, {}, ServiceType.AssessmentService)
        .then((response) => {
          if (response.success || response.status === responseStatus.success) {
            return response.data?.seniority.comments ?? [];
          }
          return [];
        })
    );
  }

  putComments(payload: PayloadComment, commentId: number) {
    const path = `/${commentId}`;
    return from(
      this.gateway
        .doPut<CommentInterface>(path, payload, ServiceType.CommentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              data: response.data,
            };
          }
          return {
            success: false,
            data: response.data,
          };
        })
    );
  }

  postComments(payload: PayloadComment) {
    const path = '';
    return from(
      this.gateway
        .doPost<CommentInterface>(path, payload, ServiceType.CommentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              data: response.data,
            };
          }
          return {
            success: false,
            data: response,
          };
        })
        .catch((error) => {
          return {
            success: true,
            data: error,
          };
        })
    );
  }

  postBehaviorComment(
    comment: PostBehaviorComment,
    assessmentExecutionId: number
  ): Observable<BehaviorComment[] | null> {
    const payload: SuperCommentEndpointPayload = {
      generalComments: [],
      behaviorComments: [comment],
      attachments: [],
    };

    const path = getAssessmentExecutionsSuperByAssessmentId(assessmentExecutionId);
    return from(
      this.gateway
        .doPost(path, payload, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === responseStatus.success) {
            return response.data.behaviorComments;
          }
          return {
            success: false,
          };
        })
    );
  }

  postAttachmentForBehavior(payload: PayloadAttachment) {
    const path = '';
    return from(
      this.gateway
        .doPost<AttachmentInterface>(path, payload, ServiceType.AttachmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              data: response.data,
            };
          }
          return {
            success: true,
            data: response.data,
          };
        })
        .catch(() => {
          return {
            success: false,
            data: {},
          };
        })
    );
  }

  putAttachmentForBehavior(payload: PayloadAttachment, attachmentId: number) {
    const path = `/attachment/${attachmentId}`;
    return from(
      this.gateway
        .doPut<AttachmentInterface>(path, payload, ServiceType.CommentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              data: response.data,
            };
          }
          return {
            success: true,
            data: mockAttachment,
          };
        })
        .catch(() => {
          return {
            success: true,
            data: mockAttachment,
          };
        })
    );
  }

  getBehaviorsAttachments(
    assessmentExecutionId: number,
    seniorityId: number
  ): Observable<BehaviorAttachment[] | null> {
    const path = [
      getAssessmentExecutionsByAssessmentExecutionId(assessmentExecutionId),
      URL_QUERY_START,
      FILTER_SENIORITY_BEHAVIOR_COMMENTS,
      seniorityId,
      AMPERSAND_STRING,
      GET_BEHAVIORS_ATTACHMENTS,
    ].join(EMPTY_STRING);

    return from(
      this.gateway
        .doGet(path, {}, ServiceType.AssessmentService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === responseStatus.success) {
            return response.data.seniority.attachments;
          }
          return {
            success: false,
          };
        })
    );
  }

  getComment(payload: PayloadCommentFilter[]): Observable<CommentsInterface> {
    const path = 'api/v2/comment/get-filter-comments';
    const dataPayload = {
      data: payload,
    };
    return from(
      this.gateway
        .doPost<CommentsInterface>(path, dataPayload, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              data: response.data,
            };
          }
          return {
            success: true,
            data: response.data,
          };
        })
        .catch(() => {
          return {
            success: true,
            data: [],
          };
        })
    );
  }

  getAttachment(filter: PayloadAttachmentFilter[]): Observable<AttachmentsInterface> {
    const path = 'api/v2/attachment/get-attachment';
    const payload = {
      data: filter,
    };
    return from(
      this.gateway
        .doPost<AttachmentsInterface>(path, payload, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              data: response.data,
            };
          }
          return {
            success: true,
            data: response.data,
          };
        })
        .catch((error) => {
          return {
            success: false,
            data: [],
          };
        })
    );
  }

  getAdditionalComments(assessmentExecutionId: number): Observable<AssessmentComments | null> {
    const path = `${GET_ASSESSMENT_COMMENTS_PATH}${SLASH_STRING}${assessmentExecutionId}${URL_QUERY_START}${GET_GENERAL_COMMENT}${GET_ADDITIONAL_COMMENTS}`;

    return from(
      this.gateway
        .doGet<AssessmentComments | null>(path, {}, ServiceType.AdministrationService)
        .then((response) => {
          if (response.success || response.status === responseStatus.success) {
            return response.data ?? null;
          }
          return null;
        })
    );
  }

  postAdditionalComments(generalComments: any): Observable<any[]> {
    const payload: any = generalComments;
    const path = PUT_ASSESSMENT_APPROVE_PATH;

    // Non-invasive runtime logging for debugging payloads sent to approval-leader
    try {
      console.debug('[DEBUG] approval-leader path ->', path);
      console.debug('[DEBUG] approval-leader payload ->', payload);
    } catch (e) {
      // swallow any logging errors to avoid affecting runtime
    }

    return from(
      this.gateway.doPut<any>(path, payload, ServiceType.AdministrationService).then((response) => {
        if (response.success || response.status === responseStatus.success) {
          return response.data ?? [];
        }
        return [];
      })
    );
  }

  rejectAssesment(generalComments: any): Observable<any[]> {
    const payload: any = generalComments;

    const path = PUT_ASSESSMENT_REJECT_PATH;

    return from(
      this.gateway.doPut<any>(path, payload, ServiceType.AdministrationService).then((response) => {
        if (response.success || response.status === responseStatus.success) {
          return response.data ?? [];
        }
        return [];
      })
    );
  }

  getLastValoration(payload: PayloadGetLastValoration): Observable<Valoration> {
    const path = GET_LAST_VALORATION_PATH;
    return from(
      this.gateway
        .doPost<Valoration>(path, payload, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data[0];
          }
        })
        .catch((error) => {
          return error;
        })
    );
  }

  getInstanceValoration(payload: PayloadGetInstanceValoration): Observable<Valoration> {
    const path = GET_INSTANCE_VALORATION_PATH;
    return from(
      this.gateway
        .doPost<Valoration>(path, payload, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data;
          }
        })
    );
  }

  public sendResultPragmatic(
    payload: SendResultPragmaticRequest
  ): Observable<SendResultPragmaticResponse> {
    if (!payload.no_growth) {
      payload.no_growth = {};
    }

    return from(
      this.gateway
        .doPut<SendResultPragmaticResponse>(
          PUT_SEND_RESULT_PRAGMATIC_PATH,
          payload,
          ServiceType.AdministrationService
        )
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data ?? ({} as SendResultPragmaticResponse);
          }
          return {} as SendResultPragmaticResponse;
        })
    );
  }

  finalizedAutovaloration(payload: PayloadFinalizedAutovaloration): Observable<any> {
    const path = POST_FINALIZED_AUTOVALORATION;
    return from(
      this.gateway
        .doPut<Valoration>(path, payload, ServiceType.MapRatingService)
        .then((response: doGetResponseType) => {
          return {
            success: response.success,
            status: response.status,
            data: response.data,
          };
        })
        .catch(() => {
          return {
            success: false,
            data: [],
          };
        })
    );
  }

  getAssessmentTraceability(assessmentExecutionId: number): Observable<AssessmentTraceabilityEvent[]> {
    const path = getAssessmentTraceabilityPath(assessmentExecutionId);
    return from(
      this.gateway
        .doGet<AssessmentTraceabilityResponse>(path, {}, ServiceType.MapRatingService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data?.events ?? [];
          }
          return [];
        })
        .catch(() => [])
    );
  }
}
